import React from 'react';
import { useQuery } from '@tanstack/react-query';

import { CONFIG } from 'constants/config';
import { QUERY_KEYS } from 'constants/query-keys';
import { createQueryKey } from 'helpers/ReactQueryHelpers';
import { useIsAuthenticated } from 'hooks/useIsAuthenticated';
import { getActiveOrder } from 'services/OrdersService';

import type {
  INotice, IOrder, IOrderResponse, IOrderResponseNotice, IUnauthenticatedOrder, ReactQueryOptions
} from 'types';

export function useActiveOrder<S = IOrder>(
  queryOptions: ReactQueryOptions<IOrderResponse, S> = {}
) {
  const {
    //
    isSettled: isAuthSettled,
    isFetching: isAuthFetching,
    isAuthenticated,
    //
    accessToken,
    //
  } = useIsAuthenticated();

  const isAuthenticatedOrder = !!accessToken;

  const select = React.useCallback((data: IOrderResponse) => adapter(data, queryOptions?.select), [queryOptions.select]);

  const query = useQuery({
    queryKey: getKey({ isAuthenticatedOrder }),
    queryFn: () => getActiveOrder(accessToken),
    staleTime: CONFIG.DURATIONS.DEFAULT_QUERY_STALE_TIME,
    ...queryOptions,
    select
  });

  return {
    ...query,
    isIdle: query.fetchStatus === 'idle',
    isLoading: (
      query.isLoading
      || isAuthFetching
      || !isAuthSettled
      || (isAuthSettled && isAuthenticated && !!(query?.data as IUnauthenticatedOrder)?.isNotCreated)
    )
  };
}

// Helpers

// --- Key

interface GetKeyParams {
  isAuthenticatedOrder?: boolean
}

export const getKey = (params?: GetKeyParams) => {
  const {
    isAuthenticatedOrder
  } = params || {};

  if (typeof isAuthenticatedOrder === 'undefined') {
    return createQueryKey([
      QUERY_KEYS.ACTIVE_ORDER,
    ]);
  }

  return createQueryKey([
    QUERY_KEYS.ACTIVE_ORDER,
    isAuthenticatedOrder ? 'authenticated' : 'unauthenticated'
  ]);
};

// --- Adapter

function adapter<S = IOrderResponse>(
  data: IOrderResponse,
  select?: (data: IOrderResponse) => S
): S | IOrder {
  if (!select) {
    return toOrder(data);
  }
  return select(data);
}

function toOrder(data: IOrderResponse): IOrder {
  return data;

  // const {
  //   cartNoticeV2,
  //   checkoutNoticeV2,
  //   ...rest
  // } = data;

  // return {
  //   ...rest,
  //   cartNotices: toOrderNotices(cartNoticeV2),
  //   checkoutNotices: toOrderNotices(checkoutNoticeV2),
  // };
}

export function toOrderNotices(data: IOrderResponseNotice[] | undefined): INotice[] {
  if (!data) {
    return [];
  }

  return data.map((notice) => {
    const {
      //
      title,
      icon,
      description,
      //
      url,
      //
      type,
      //
    } = notice || {};

    return {
      details: {
        //
        title,
        description,
        icon,
        //
        type,
        //
      },
      action: !url ? undefined : {
        type: 'link',
        url,
      }
    };
  });
}
